import { ErrorBoundary } from '@/components/Rollbar';
import { useDefaultPagination } from '@/features/approving_invoice_reports/components/PaymentDetailDrawer/Detail/LeftContent/TransactionsDetail/hooks/useInvoicesTransaction';
import { useTranslation } from '@/i18n';

import { CardTableSkeleton, type TColumnsPerRow } from '@/components/CardTable';
import { Wrapper } from '@/components/OpenFeature/Wrapper';
import { useGetTransactionProperties } from '@/context/services/invoice_report/TransactionProperties.service';
import { usePaymentDetail } from '@/features/approving_invoice_reports/components/PaymentDetailDrawer/context/PaymentDetailContext';
import { TransactionsDetailCards } from '@/features/approving_invoice_reports/components/PaymentDetailDrawer/Detail/LeftContent/TransactionsDetail/Cards/TransactionsDetailCards';
import { ControlArea } from '@/features/approving_invoice_reports/components/PaymentDetailDrawer/Detail/LeftContent/TransactionsDetail/ControlArea';
import { useApprovingInvoiceReportTransactionsPaged } from '@/features/approving_invoice_reports/components/PaymentDetailDrawer/Detail/LeftContent/TransactionsDetail/hooks/useApprovingInvoiceReportTransactionsPaged';
import { useInvoiceTransactionColumns } from '@/features/approving_invoice_reports/components/PaymentDetailDrawer/Detail/LeftContent/TransactionsDetail/hooks/useInvoiceTransactionColumns';
import {
  TransactionsLayout,
  TransactionsLayouts,
  useTransactionsLayout,
} from '@/features/approving_invoice_reports/components/PaymentDetailDrawer/Detail/LeftContent/TransactionsDetail/hooks/useTransactionsLayout';
import { ShowRemainingButton } from '@/components/InvoiceReportDetailDrawer/parts/ShowRemainingButton';
import {
  type InternalTableProps,
  VirtualTable,
} from '@/components/InvoiceReportDetailDrawer/parts/VirtualTable';
import { ErrorTransactionsDetailTable } from '@/features/approving_invoice_reports/components/PaymentDetailDrawer/Detail/LeftContent/TransactionsDetail/Table/ErrorTransactionsDetailTable';
import { LoadingTransactionsDetailTable } from '@/features/approving_invoice_reports/components/PaymentDetailDrawer/Detail/LeftContent/TransactionsDetail/Table/LoadingTransactionsDetailTable';
import type { InvoiceTransactionRow } from '@/features/approving_invoice_reports/components/PaymentDetailDrawer/Detail/LeftContent/TransactionsDetail/hooks/useInvoiceTransactionColumns';
import { getScrollableAncestor } from '@/utils/dom/getScrollableAncestor';
import {
  FC,
  memo,
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';

// `useLayoutEffect` restores scroll before paint (no visible jump) but warns
// under SSR; fall back to `useEffect` on the server.
const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;

type TransactionsDetailLayoutSwitchProps = {
  reportId: string;
  /** Column dataIndex values to exclude from the transactions table */
  excludeColumns?: string[];
};

/**
 * `approval-drawer-view-optimization` ON: the approving-namespace endpoint
 * plus the card/table toggle.
 */
const SHOW_REMAINING_THRESHOLD = 10;

/** 支払明細 card columns per row when the invoice preview pane is visible. */
const TRANSACTIONS_CARD_COLUMNS_WITH_PREVIEW: TColumnsPerRow = 3;
/** 支払明細 card columns per row when there is no invoice preview shown. */
const TRANSACTIONS_CARD_COLUMNS_WITHOUT_PREVIEW: TColumnsPerRow = 6;

type InnerListProps = TransactionsDetailLayoutSwitchProps & {
  layout: TransactionsLayout;
  setLayout: (layout: TransactionsLayout) => void;
};

const InnerList: FC<InnerListProps> = memo(
  ({ reportId, excludeColumns, layout, setLayout }) => {
    const { isRightPaneCollapsed } = usePaymentDetail();
    const columnsPerRow: TColumnsPerRow = isRightPaneCollapsed
      ? TRANSACTIONS_CARD_COLUMNS_WITHOUT_PREVIEW
      : TRANSACTIONS_CARD_COLUMNS_WITH_PREVIEW;

    // Card and table layouts have very different heights (a virtualized card
    // list reserves far more than the compact table), so swapping them shifts
    // the drawer's scrollable content and the browser clamps the scroll — the
    // pane appears to jump to the top. Capture the scroll container's position
    // on switch and restore it before paint so toggling layouts stays put.
    const sentinelRef = useRef<HTMLSpanElement>(null);
    const scrollElRef = useRef<Element | null>(null);
    const savedScrollTopRef = useRef<number | null>(null);

    const handleLayoutChange = useCallback(
      (next: TransactionsLayout) => {
        const scrollEl = getScrollableAncestor(sentinelRef.current);
        scrollElRef.current = scrollEl;
        savedScrollTopRef.current = scrollEl ? scrollEl.scrollTop : null;
        setLayout(next);
      },
      [setLayout]
    );

    useIsomorphicLayoutEffect(() => {
      const scrollEl = scrollElRef.current;
      const saved = savedScrollTopRef.current;
      if (scrollEl && saved !== null) {
        scrollEl.scrollTop = saved;
        savedScrollTopRef.current = null;
      }
    }, [layout]);

    const {
      data: rawData,
      totalRecords,
      isExpanded,
      isLoadingMore,
      showRemaining,
      collapse,
    } = useApprovingInvoiceReportTransactionsPaged(reportId);
    // `id` is optional on the auto-generated type; back-fill it for rows where
    // the API omitted it, same as the applicant-facing hook this replaces did.
    const data = useMemo(
      () =>
        (rawData ?? []).map((item, index) => ({
          ...item,
          id: item.id || `${reportId}-${index}`,
        })),
      [rawData, reportId]
    );
    // Fetch fields that will be used to generate dynamic columns
    const { data: dynamicTransactionFields } =
      useGetTransactionProperties(reportId);

    const columns = useInvoiceTransactionColumns(
      reportId,
      data,
      dynamicTransactionFields,
      true
    );

    const filteredColumns = useMemo(() => {
      if (!excludeColumns || excludeColumns.length === 0) return columns;
      return columns.filter((col) => {
        if (typeof col.dataIndex === 'string') {
          return !excludeColumns.includes(col.dataIndex);
        }
        return true;
      });
    }, [columns, excludeColumns]);

    const { t } = useTranslation();
    const pagination = useDefaultPagination();

    const table: InternalTableProps<InvoiceTransactionRow> = useMemo(() => {
      return {
        data,
        columns: filteredColumns ?? [],
        emptyText: <>{t('transactions_detail_empty_message')}</>,
      };
    }, [data, filteredColumns, t]);

    const showRemainingButton =
      totalRecords > SHOW_REMAINING_THRESHOLD ? (
        <ShowRemainingButton
          isExpanded={isExpanded}
          isLoading={isLoadingMore}
          onShowRemaining={showRemaining}
          onCollapse={collapse}
          collapseLabel={t('approving_invoice_reports.transactions.collapse')}
          showAllLabel={t('approving_invoice_reports.transactions.show_all')}
          testIdPrefix='transactions-detail'
        />
      ) : undefined;

    return (
      <>
        {/* Zero-size anchor used only to locate the scroll container. */}
        <span
          ref={sentinelRef}
          aria-hidden='true'
          style={{ display: 'none' }}
        />
        <ControlArea
          count={totalRecords}
          layout={layout}
          onLayoutChange={handleLayoutChange}
        />
        {layout === TransactionsLayouts.card ? (
          <TransactionsDetailCards
            columns={filteredColumns}
            data={data}
            totalRecords={totalRecords}
            footer={showRemainingButton}
            columnsPerRow={columnsPerRow}
          />
        ) : (
          <VirtualTable
            table={table}
            pagination={pagination}
            footer={showRemainingButton}
            testId='transactions-detail-table'
          />
        )}
      </>
    );
  }
);
InnerList.displayName = 'TransactionsDetailLayoutSwitchInnerList';

export const TransactionsDetailLayoutSwitch: FC<TransactionsDetailLayoutSwitchProps> =
  memo(({ reportId, excludeColumns }) => {
    // Owned here so the fallback can match the layout; reads localStorage
    // (NA-5531), never suspends.
    //
    // Must stay a SINGLE instance passed down to `InnerList` — `useLayoutPreference`
    // holds the layout in `useState`, so a second call there would leave this copy
    // stuck at its mount value. The drawer swaps `reportId` without remounting (see
    // `useApprovingInvoiceReportTransactionsPaged`), so a stale value here would show
    // the table skeleton to a card-layout user: the exact bug this ticket fixes.
    const { layout, setLayout } = useTransactionsLayout();

    return (
      <Wrapper isProto={false}>
        <ErrorBoundary fallback={ErrorTransactionsDetailTable}>
          <Suspense
            fallback={
              layout === TransactionsLayouts.card ? (
                <CardTableSkeleton testId='transactions-detail-cards-skeleton' />
              ) : (
                <LoadingTransactionsDetailTable />
              )
            }
          >
            <InnerList
              reportId={reportId}
              excludeColumns={excludeColumns}
              layout={layout}
              setLayout={setLayout}
            />
          </Suspense>
        </ErrorBoundary>
      </Wrapper>
    );
  });
TransactionsDetailLayoutSwitch.displayName = 'TransactionsDetailLayoutSwitch';
